import type { DiscoverySearchQuery, PublicVenue } from './domain'
import { isDiscoverableEvent, isDiscoveryExperience } from './discovery-index'
import { compareVenueNetwork, externalBookingChannel } from './venue-source'

export type DiscoveryFilterOptions = {
  cuisines: Array<{ label: string; count: number }>
  neighborhoods: Array<{ label: string; count: number }>
  prices: Array<{ label: '$' | '$$' | '$$$' | '$$$$'; count: number }>
}

export function discoveryFilterOptions(venues: PublicVenue[]): DiscoveryFilterOptions {
  return {
    cuisines: counted(venues.flatMap((venue) => [venue.cuisine, ...(venue.cuisineDescriptors ?? [])].filter((value): value is string => Boolean(value)))),
    neighborhoods: counted(venues.flatMap((venue) => venue.neighborhood ? [venue.neighborhood] : [])),
    prices: counted(venues.flatMap((venue) => venue.priceRange ? [venue.priceRange] : []))
      .filter((item): item is { label: '$' | '$$' | '$$$' | '$$$$'; count: number } => isPrice(item.label))
      .sort((left, right) => left.label.length - right.label.length),
  }
}

export function filterDiscoveryVenues(venues: PublicVenue[], query: DiscoverySearchQuery) {
  const keyword = normalize(query.q)
  const cuisines = new Set(query.cuisines.map(normalize))
  const neighborhoods = new Set(query.neighborhoods.map(normalize))

  return venues
    .filter((venue) => !keyword || searchableVenueText(venue).includes(keyword))
    .filter((venue) => cuisines.size === 0 || [venue.cuisine, ...(venue.cuisineDescriptors ?? [])].some((value) => cuisines.has(normalize(value ?? ''))))
    .filter((venue) => query.prices.length === 0 || (venue.priceRange ? query.prices.includes(venue.priceRange) : false))
    .filter((venue) => neighborhoods.size === 0 || neighborhoods.has(normalize(venue.neighborhood ?? '')))
    .filter((venue) => matchesBookingType(venue, query.bookingType))
    .sort((left, right) => sortVenues(left, right, query.sort))
}

export function hasDistinctExperience(venue: PublicVenue) {
  return venue.offerings.some((offering) => isPublicOffering(offering.access) && isDiscoveryExperience(offering))
}

function searchableVenueText(venue: PublicVenue) {
  return normalize([
    venue.publicName,
    venue.cuisine,
    ...(venue.cuisineDescriptors ?? []),
    venue.neighborhood,
    venue.market,
    venue.description,
    ...venue.offerings.flatMap((offering) => [offering.name, offering.description]),
    ...venue.events.flatMap((event) => [event.name, event.description]),
  ].filter(Boolean).join(' '))
}

function matchesBookingType(venue: PublicVenue, type: DiscoverySearchQuery['bookingType']) {
  if (type === 'reservations') {
    const external = externalBookingChannel(venue)
    return external?.destination === 'booking' || venue.offerings.some((offering) => isPublicOffering(offering.access) && !isDiscoveryExperience(offering))
  }
  if (type === 'experiences') return hasDistinctExperience(venue)
  if (type === 'events') return venue.events.some((event) => isDiscoverableEvent(event, venue.timezone))
  if (type === 'private') return venue.privateEventsEnabled
  return true
}

const isPublicOffering = (access: PublicVenue['offerings'][number]['access']) => access === undefined || access === 'public'

function sortVenues(left: PublicVenue, right: PublicVenue, sort: DiscoverySearchQuery['sort']) {
  const networkOrder = compareVenueNetwork(left, right)
  if (networkOrder !== 0) return networkOrder
  if (sort === 'name') return left.publicName.localeCompare(right.publicName)
  if (sort === 'distance') return (left.distanceKm ?? Number.POSITIVE_INFINITY) - (right.distanceKm ?? Number.POSITIVE_INFINITY)
  return 0
}

function counted(values: string[]) {
  const counts = new Map<string, { label: string; count: number }>()
  values.forEach((label) => {
    const key = normalize(label)
    const current = counts.get(key)
    counts.set(key, { label: current?.label ?? label, count: (current?.count ?? 0) + 1 })
  })
  return [...counts.values()].sort((left, right) => left.label.localeCompare(right.label))
}

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9$]+/g, ' ').trim()

function isPrice(value: string): value is '$' | '$$' | '$$$' | '$$$$' {
  return value === '$' || value === '$$' || value === '$$$' || value === '$$$$'
}
